import React, { useRef, useEffect, useState, useMemo } from "react";
import { StriimInputField, StriimForm, StriimTypography, StriimMessageBox } from "@striim/striim-ui";
import * as Yup from "yup";
import { Box, Grid } from "@mui/material";

import VaultsService from "./vaults-service";
import { mapOptions } from "../utils";
import { VaultTypes } from "./constants";
import CollapsibleSection from "src/generic/collapsible-section/collapsible-section";

const sxStyles = {
    container: {
        marginBottom: 2
    },
    infoBoxContainer: {
        "& .MuiCard-root": {
            backgroundColor: ({ palette }) => palette.greyscale[100],
            borderColor: ({ palette }) => palette.greyscale[200],
            "& .MuiCardHeader-avatar": {
                "& svg": {
                    fill: ({ palette }) => palette.greyscale[600]
                }
            }
        }
    },
    grid: {
        columnGap: 3,
        justifyContent: "space-between"
    },
    advancedInput: {
        mt: 1
    }
};

const DEFAULT_VALUES = {
    name: "",
    nsName: "",
    properties: {
        VaultType: VaultTypes.AWS_SECRETS_MANAGER,
        Region: "",
        AccessKeyID: "",
        SecretAccessKey: "",
        SessionToken: "",
        ConnectionTimeout: 30000,
        ReadTimeout: 60000,
        MaxRetries: 3
    }
};

const getValidationSchema = isEditMode =>
    Yup.object().shape({
        name: Yup.string().required("Name is required"),
        nsName: Yup.string().required("Namespace is required"),
        properties: Yup.object({
            Region: Yup.string(),
            AccessKeyID: Yup.string(),
            SecretAccessKey: isEditMode ? Yup.string().nullable() : Yup.string(),
            ConnectionTimeout: Yup.number()
                .positive("Value must be positive")
                .integer("Must be an integer"),
            ReadTimeout: Yup.number()
                .positive("Value must be positive")
                .integer("Must be an integer"),
            MaxRetries: Yup.number()
                .positive("Value must be positive")
                .integer("Must be an integer")
        })
    });

const AWSSecretsManager = ({ setFormValues, setIsValid, dataForEdit }) => {
    const [options, setOptions] = useState([]);
    const [secretAccessKeyInput, setSecretAccessKeyInput] = useState(false);

    const values = dataForEdit || DEFAULT_VALUES;

    useEffect(() => {
        async function fetchData() {
            const theData = await VaultsService.getNamespaces();
            setOptions(theData?.models);
        }

        fetchData();
    }, []);

    const getOptions = useMemo(() => mapOptions(options), [options]);

    const formRef = useRef();

    const handleSecretAccessKeyField = event => {
        if (event.target.value === "") {
            setSecretAccessKeyInput(false);
        }
    };

    const validationSchema = getValidationSchema(!!dataForEdit);

    return (
        <StriimForm
            values={values}
            validationSchema={validationSchema}
            formRef={formRef}
            validateOnMount={true}
            validateOnChange={true}
        >
            {({ values, isValid, dirty }) => {
                setFormValues(values);
                setIsValid(dataForEdit ? isValid && dirty : isValid);
                return (
                    <>
                        <StriimInputField
                            required
                            name="name"
                            label="Name"
                            placeholder="Enter Vault name"
                            isFormElement
                            sx={sxStyles.container}
                            disabled={!!dataForEdit}
                        />
                        <StriimInputField
                            required
                            name="nsName"
                            label="Namespace"
                            select
                            id="data-test-id-namespace-select"
                            placeholder="Namespace"
                            SelectProps={{ options: getOptions, menuPosition: "fixed" }}
                            isFormElement
                            sx={sxStyles.container}
                            disabled={!!dataForEdit}
                        />
                        <Box sx={[sxStyles.infoBoxContainer, sxStyles.container]}>
                            <StriimMessageBox
                                type="INFO"
                                text={
                                    <StriimTypography variant="body4" color="greyscale.900">
                                        When running on EC2/ECS/EKS with an IAM role, you can leave Region, Access Key
                                        ID, and Secret Access Key empty. Credentials and region will be automatically
                                        resolved from the instance metadata.
                                    </StriimTypography>
                                }
                            />
                        </Box>
                        <StriimInputField
                            name="properties.Region"
                            label="Region"
                            helperText='Example: "us-west-2"'
                            isFormElement
                            sx={sxStyles.container}
                        />
                        <Grid container sx={{ ...sxStyles.container, ...sxStyles.grid }}>
                            <Grid item xs={5.8}>
                                <StriimInputField
                                    name="properties.AccessKeyID"
                                    label="Access Key ID"
                                    helperText='Leave empty to use IAM role. Example: "AKIA..."'
                                    isFormElement
                                />
                            </Grid>
                            <Grid item xs={5.8}>
                                <div
                                    data-test-id="vaults-encrypted-data-field"
                                    onClick={() => {
                                        if (dataForEdit) {
                                            setSecretAccessKeyInput(true);
                                        }
                                    }}
                                >
                                    <StriimInputField
                                        name="properties.SecretAccessKey"
                                        label="Secret Access Key"
                                        autoFocus={secretAccessKeyInput}
                                        isFormElement
                                        type="password"
                                        placeholder={dataForEdit ? "(encrypted)" : null}
                                        disabled={dataForEdit ? !secretAccessKeyInput : false}
                                        previewPassword
                                        helperText={
                                            dataForEdit
                                                ? "Click to enter a new secret access key"
                                                : "Leave empty to use IAM role"
                                        }
                                        onChange={handleSecretAccessKeyField}
                                        onBlur={handleSecretAccessKeyField}
                                    />
                                </div>
                            </Grid>
                        </Grid>
                        <StriimInputField
                            name="properties.SessionToken"
                            label="Session Token"
                            helperText="Required only when using temporary credentials"
                            isFormElement
                            sx={sxStyles.container}
                            type="password"
                            previewPassword
                        />
                        <CollapsibleSection label={"Advanced Settings"} title="Advanced Settings">
                            <Grid container sx={{ ...sxStyles.container, ...sxStyles.grid }}>
                                <Grid item xs={3.8}>
                                    <StriimInputField
                                        name="properties.ConnectionTimeout"
                                        label="Connection Timeout (ms)"
                                        isFormElement
                                        type="number"
                                        sx={sxStyles.advancedInput}
                                    />
                                </Grid>
                                <Grid item xs={3.8}>
                                    <StriimInputField
                                        name="properties.ReadTimeout"
                                        label="Read Timeout (ms)"
                                        isFormElement
                                        type="number"
                                        sx={sxStyles.advancedInput}
                                    />
                                </Grid>
                                <Grid item xs={3.8}>
                                    <StriimInputField
                                        name="properties.MaxRetries"
                                        label="Max Retries"
                                        isFormElement
                                        type="number"
                                        sx={sxStyles.advancedInput}
                                    />
                                </Grid>
                            </Grid>
                        </CollapsibleSection>
                    </>
                );
            }}
        </StriimForm>
    );
};

export default AWSSecretsManager;

